<script setup lang="ts">
import { FontSetting } from "@/models/FontSetting";
import { LyricPreferences } from "@/models/LyricPreferences";
import { WatchHandle } from "vue";
const store = useSongStore();
const profile = useProfileStore();
const shareStore = useShareStore();
const api = useApiStore();
const filename = computed(
  () => sanitizeFilename(store.song?.title || "") + "-lyrics",
);

const headingsFontFamily = computed(() => {
  let family = AvailableFontFamilies[0];
  if (profile.profile?.lyricPreferences.fontSettings.headings.family) {
    family =
      AvailableFontFamilies.find(
        (f) =>
          f.type ===
          profile.profile!.lyricPreferences.fontSettings.headings.family,
      ) || AvailableFontFamilies[0];
  }
  loadFont(family.id, family.url);
  return family;
});

const lyricsFontFamily = computed(() => {
  let family = AvailableFontFamilies[0];
  if (profile.profile?.lyricPreferences.fontSettings.lyrics.family) {
    family =
      AvailableFontFamilies.find(
        (f) =>
          f.type ===
          profile.profile!.lyricPreferences.fontSettings.lyrics.family,
      ) || AvailableFontFamilies[0];
  }
  loadFont(family.id, family.url);
  return family;
});

const fontSettings = ref<{
  headings: FontSetting;
  lyrics: FontSetting;
}>({
  headings: {
    size: profile.profile?.lyricPreferences.fontSettings.headings.size || "16",
    color:
      profile.profile?.lyricPreferences.fontSettings.headings.color ||
      AvailableTextColors[0].name,
    highlight:
      profile.profile?.lyricPreferences.fontSettings.headings.highlight ||
      AvailableHighlightColors[0].name,
    family: headingsFontFamily.value.type,
  },
  lyrics: {
    size: profile.profile?.lyricPreferences.fontSettings.lyrics.size || "16",
    color:
      profile.profile?.lyricPreferences.fontSettings.lyrics.color ||
      AvailableTextColors[0].name,
    family: lyricsFontFamily.value.type,
  },
});

const fontSize = ref(
  profile.profile?.lyricPreferences.fontSize.toString() || "16",
);
const textAlignment = ref(
  profile.profile?.lyricPreferences.textAlignment || "Left",
);
const twoColMode = ref(profile.profile?.lyricPreferences.twoColMode || false);

function getProfileSettings() {
  fontSettings.value = {
    headings: {
      size:
        profile.profile?.lyricPreferences.fontSettings.headings.size || "16",
      color:
        profile.profile?.lyricPreferences.fontSettings.headings.color ||
        AvailableTextColors[0].name,
      highlight:
        profile.profile?.lyricPreferences.fontSettings.headings.highlight ||
        AvailableHighlightColors[0].name,
      family: headingsFontFamily.value.type,
    },
    lyrics: {
      size: profile.profile?.lyricPreferences.fontSettings.lyrics.size || "16",
      color:
        profile.profile?.lyricPreferences.fontSettings.lyrics.color ||
        AvailableTextColors[0].name,
      family: lyricsFontFamily.value.type,
    },
  };

  fontSize.value =
    profile.profile?.lyricPreferences.fontSize.toString() || "16";
  textAlignment.value =
    profile.profile?.lyricPreferences.textAlignment || "Left";
  twoColMode.value = profile.profile?.lyricPreferences.twoColMode || false;
}

async function copyLyrics() {
  await store.copyToClipboard(store.copyLyricsContent);
  store.onLyricsActivityDebounced("copied");
  //toast.successToast(t("SV.LIT_CopiedToClipboard"));
}

async function download() {
  if (!store.data) {
    return;
  }
  await downloadTextFile(store.copyLyricsContent, filename.value);
  store.onLyricsActivityDebounced("downloaded");
}

function share() {
  if (!store.data) {
    return;
  }
  shareStore.shareTextFile(store.copyLyricsContent, filename.value);
  store.onLyricsActivityDebounced("shared");
}

function onPrint() {
  window.print();
  store.onLyricsActivityDebounced("printed");
}

const showBusy = ref({
  copy: false,
  print: false,
  download: false,
  share: false,
});

const shownBusy = computed(() => ({
  email: store.emailSettings.busy,
  ...showBusy.value,
}));

function reset() {
  fontSize.value = "16";
  textAlignment.value = "Left";
  twoColMode.value = false;
  fontSettings.value = {
    headings: {
      size: "16",
      color: AvailableTextColors[0].name,
      highlight: AvailableHighlightColors[0].name,
      family: AvailableFontFamilies[0].type,
    },
    lyrics: {
      size: "16",
      color: AvailableTextColors[0].name,
      family: AvailableFontFamilies[0].type,
    },
  };
}

let unwatchHandle: WatchHandle | null = null;
onMounted(async () => {
  await waitUntilTrue(() => !!profile.profile);
  getProfileSettings();
  unwatchHandle = watch(
    () => [fontSettings, fontSize, textAlignment, twoColMode],
    debouncedSavedSetting,
    {
      deep: true,
    },
  );
});

onUnmounted(() => {
  unwatchHandle?.();
});

function emailSong() {
  store.emailSong({
    product: "lyrics",
    columns: null,
    noteSize: null,
    octave: null,
    orientation: null,
    paperSize: null,
    songKey: null,
    style: null,
  });
}

const beta = useBetaStore();
const enableFontSettingsFeature = computed(
  () => beta.features.lyricFontSettings,
);

const debouncedSavedSetting = useDebounceFn(saveSetting, 500);

function saveSetting() {
  if (profile.profile) {
    const settings: LyricPreferences = {
      fontSize: parseInt(fontSize.value),
      textAlignment: textAlignment.value,
      twoColMode: twoColMode.value,
      fontSettings: fontSettings.value,
    };
    api.post("/UpdateLyricPreferences", {
      lyricPreferences: JSON.stringify(settings),
    });
    profile.profile.lyricPreferences = settings;
  }
}
</script>

<template>
  <PageWrapper no-body>
    <FullScreenWrapper>
      <template #header>
        <SongDetailsAlertContentError
          v-if="store.status === LoadStatus.Error"
          no-btn
        />
        <SongDetailsAlertNotAuthenticated
          v-else-if="
            store.status === LoadStatus.Ready &&
            store.data?.products.lyrics.noAuthReason ===
              NoAuthReason.NotAuthenticated
          "
        />
        <SongDetailsAlertUpgradeSubscription
          v-else-if="
            store.status === LoadStatus.Ready &&
            store.data?.products.lyrics.noAuthReason ===
              NoAuthReason.RequiresSubscriptionUpgrade
          "
        />
        <SongDetailsAlertSubscriptionRequired
          v-else-if="
            store.status === LoadStatus.Ready &&
            store.data?.products.lyrics.noAuthReason ===
              NoAuthReason.LyricsRequireSsSubscription
          "
        />
      </template>
      <MusicSheetLyrics
        :font-size="fontSize"
        :text-alignment="textAlignment"
        :two-col-mode="twoColMode"
        :font-settings="
          (enableFontSettingsFeature && fontSettings) || undefined
        "
        @copy="store.onLyricsActivityDebounced('copied')"
      />

      <template #toolbar>
        <ToolbarTextAlign v-model="textAlignment" />
        <ToolbarSeparator />
        <template v-if="!enableFontSettingsFeature">
          <ToolbarFontSize
            v-model="fontSize"
            lyrics
          />
          <ToolbarSeparator />
        </template>
        <ToolbarColumns v-model="twoColMode" />
        <template v-if="enableFontSettingsFeature">
          <ToolbarSeparator />
          <ToolbarFontSize
            v-model="fontSize"
            lyrics
            :title="$t('SV.LIT_Scale')"
          />
          <ToolbarSeparator />
          <ToolbarFontSettings v-model="fontSettings" />
          <ToolbarSeparator />
          <ToolbarReset @reset="reset" />
        </template>
      </template>

      <template #action-bar-controls>
        <div
          class="ga-info"
          :data-songnumber="store.song?.ccliSongNumber"
          :data-content-type="'Lyrics'"
          :data-text-alignment="textAlignment"
          :data-layoutstyle="twoColMode ? 'TwoColumn' : 'SingleColumn'"
        >
          <ToolbarActionBarControls
            enable-copy
            enable-email
            enable-share
            :enable-print="!isFirefox()"
            product="lyrics"
            :show-busy="shownBusy"
            :ids="{
              copy: 'lyricsCopyButton',
              download: 'lyricsDownloadButton',
              email: 'lyricsEmailButton',
              fullscreen: 'lyricsFullscreenButton',
              print: 'lyricsPrintButton',
              share: 'lyricsShareButton',
            }"
            @print="onPrint()"
            @copy="copyLyrics()"
            @download="download()"
            @share="share()"
            @email="emailSong"
          />
        </div>
      </template>
    </FullScreenWrapper>
  </PageWrapper>
</template>

<style scoped lang="postcss"></style>
